import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, AlertTriangle, ShieldCheck } from "lucide-react";
import { runStaffDscrQuote } from "@/lib/rate-center.functions";
import { DEFAULT_RATE_SCENARIO, type PublicRateOption } from "@/lib/rate-center";
import { groupPublicOptions, netBorrowerCost, optionIdentity } from "@/lib/rate-grouping";

export const Route = createFileRoute("/admin/pricing-groups")({
  head: () => ({
    meta: [
      { title: "Public pricing groups · Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PricingGroups,
});

const money = (n: number | null | undefined) =>
  n == null || !Number.isFinite(n) ? "—" : `$${Math.round(n).toLocaleString("en-US")}`;

function QuoteLine({ o, representative }: { o: PublicRateOption; representative: boolean }) {
  return (
    <div
      className={`rounded-lg border p-3 text-xs ${
        representative ? "border-brand bg-brand/5" : "border-border bg-background"
      }`}
    >
      <div className="flex flex-wrap items-center gap-2 font-semibold">
        {representative ? (
          <span className="rounded-full bg-brand px-2 py-0.5 text-[10px] uppercase text-primary-foreground">
            Published publicly
          </span>
        ) : (
          <span className="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase text-muted-foreground">
            Underlying quote
          </span>
        )}
        <span>{o.lenderLabel}</span>
        <span className="text-muted-foreground">{o.programLabel}</span>
      </div>
      <div className="mt-2 grid gap-1 sm:grid-cols-3">
        <div>Rate {o.rate.toFixed(3)}% · APR {o.apr.toFixed(3)}%</div>
        <div>Price {o.price ?? "—"} · Lock {o.lockPeriod ?? "—"}</div>
        <div>Payment {money(o.principalAndInterest)}</div>
        <div>Points {money(o.pointsDollar)}</div>
        <div>Credit {money(o.creditDollar)}</div>
        <div>
          {o.primaryLenderFee?.label ?? "Primary lender fee"} {money(o.primaryLenderFee?.amount)} (fees-in{" "}
          {o.primaryLenderFee?.feesInBasis ?? (o.feesIncludedInPrice ? "Yes" : "No")})
        </div>
        <div className="font-semibold">Net borrower economics {money(netBorrowerCost(o))}</div>
      </div>
      {(o.lenderFeeLines ?? []).length ? (
        <ul className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
          {(o.lenderFeeLines ?? []).map((f) => (
            <li key={`${f.label}-${f.amount}`}>
              {f.label}: {money(f.amount)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function PricingGroups() {
  const staffQuote = useServerFn(runStaffDscrQuote);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    options: PublicRateOption[];
    quotes: unknown[];
  } | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  async function run() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = (await staffQuote({ data: { ...DEFAULT_RATE_SCENARIO } })) as {
        ok: boolean;
        message?: string;
        publicPreview?: PublicRateOption[];
        quotes?: unknown[];
      };
      if (!res.ok) throw new Error(res.message ?? "Pricing run failed");
      setResult({ options: res.publicPreview ?? [], quotes: res.quotes ?? [] });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Request failed");
    } finally {
      setBusy(false);
    }
  }

  const groups = groupPublicOptions(result?.options ?? []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Public pricing groups</h1>
        <p className="text-sm text-muted-foreground">
          Staff-only. Shows exactly how the public card and list views collapse equivalent quotes,
          which quote wins on net borrower economics, and every underlying raw quote behind it.
          Nothing here changes pricing or what borrowers see.
        </p>
      </div>

      <Alert>
        <ShieldCheck className="h-4 w-4" />
        <AlertTitle>Display-only grouping</AlertTitle>
        <AlertDescription>
          Grouping is a presentation layer. Raw quotes are never merged, mutated or discarded in
          storage — the full internal pricing run is still persisted exactly as returned.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Run the sample scenario</CardTitle>
          <CardDescription>
            Purchase · FL · $500,000 value · $375,000 loan · 740 FICO · 1.20 DSCR · 30-day lock
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={run} disabled={busy}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Run pricing and group
          </Button>
        </CardContent>
      </Card>

      {error ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Request failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {result ? (
        <Card>
          <CardHeader>
            <CardTitle>
              {groups.length} public option{groups.length === 1 ? "" : "s"} from{" "}
              {result.options.length} eligible quotes
            </CardTitle>
            <CardDescription>
              Raw internal quote count for this run: {result.quotes.length}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {groups.map((g) => {
              const id = optionIdentity(g.representative);
              const expanded = !!open[id];
              return (
                <div key={id} className="rounded-xl border border-border p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-semibold">
                      {g.representative.rate.toFixed(3)}% · {g.representative.apr.toFixed(3)}% APR ·{" "}
                      {g.count} matching quote{g.count === 1 ? "" : "s"}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOpen((s) => ({ ...s, [id]: !s[id] }))}
                    >
                      {expanded ? "Hide underlying quotes" : "Inspect underlying quotes"}
                    </Button>
                  </div>
                  <div className="mt-3 space-y-2">
                    <QuoteLine o={g.representative} representative />
                    {expanded
                      ? g.members
                          .filter((m) => optionIdentity(m) !== id)
                          .map((m) => (
                            <QuoteLine key={optionIdentity(m)} o={m} representative={false} />
                          ))
                      : null}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
